import React, { useState, useEffect } from 'react';
import { incentivesApi, formatApiErrorDetail, useAuth } from '../../lib/api';
import { AppLayout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import {
  Award,
  Target,
  TrendingUp,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

export default function MyPerformancePage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const currentMonth = format(new Date(), 'yyyy-MM');
        const { data: incentives } = await incentivesApi.list({ month: currentMonth });
        // Find current user's incentive
        const myIncentive = incentives.find(i => i.user_id === user.id);
        setData(myIncentive || { target_units: user.incentive_target || 0, achieved_units: 0 });
      } catch (error) {
        toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to load performance data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#005DE8]" />
        </div>
      </AppLayout>
    );
  }

  const target = data?.target_units || 0;
  const achieved = data?.achieved_units || 0;
  const percent = target > 0 ? Math.round((achieved / target) * 100) : 0;

  const getStatusBadge = () => {
    if (percent >= 100) return <Badge className="bg-emerald-50 text-emerald-700">Target Met!</Badge>;
    if (percent >= 70) return <Badge className="bg-amber-50 text-amber-700">On Track</Badge>;
    return <Badge className="bg-red-50 text-red-700">Below Target</Badge>;
  };

  const getProgressColor = () => {
    if (percent >= 100) return 'bg-emerald-500';
    if (percent >= 70) return 'bg-amber-500';
    return 'bg-red-500';
  };

  return (
    <AppLayout>
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
            My Performance
          </h1>
          <p className="text-slate-600 mt-1">Track your monthly warranty sales target</p>
        </div>

        {/* Current Month Performance */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                <Award className="h-5 w-5 text-[#005DE8]" />
                {format(new Date(), 'MMMM yyyy')}
              </CardTitle>
              {getStatusBadge()}
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Progress */}
              <div>
                <div className="flex justify-between items-end mb-2">
                  <div>
                    <p className="text-4xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
                      {achieved}
                    </p>
                    <p className="text-sm text-slate-500">warranties sold</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-semibold text-slate-600">/ {target}</p>
                    <p className="text-sm text-slate-500">target</p>
                  </div>
                </div>
                <Progress value={Math.min(percent, 100)} className="h-4" />
                <p className="text-center mt-2 font-medium text-lg" style={{ color: percent >= 100 ? '#10B981' : percent >= 70 ? '#F59E0B' : '#EF4444' }}>
                  {percent}% achieved
                </p>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <div className="text-center p-4 bg-slate-50 rounded-xl">
                  <Target className="h-6 w-6 text-[#005DE8] mx-auto mb-2" />
                  <p className="text-2xl font-bold text-slate-900">{target - achieved > 0 ? target - achieved : 0}</p>
                  <p className="text-sm text-slate-500">remaining to target</p>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-xl">
                  <TrendingUp className="h-6 w-6 text-emerald-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-slate-900">
                    {Math.round(achieved / (new Date().getDate() || 1) * 30)}
                  </p>
                  <p className="text-sm text-slate-500">projected this month</p>
                </div>
              </div>

              {/* Motivational message */}
              <div className="text-center py-4">
                {percent >= 100 ? (
                  <p className="text-emerald-600 font-medium">
                    🎉 Congratulations! You've exceeded your target!
                  </p>
                ) : percent >= 70 ? (
                  <p className="text-amber-600 font-medium">
                    💪 Great progress! Keep pushing to hit your target!
                  </p>
                ) : (
                  <p className="text-slate-600 font-medium">
                    📈 Focus on attaching warranties to every sale!
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tips */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg" style={{ fontFamily: 'Outfit' }}>Tips to Boost Sales</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-slate-600">
              <li className="flex items-start gap-2">
                <span className="text-[#005DE8]">•</span>
                Explain the benefits of extended warranty to every customer
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#005DE8]">•</span>
                Highlight coverage for accidental damage and repairs
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#005DE8]">•</span>
                Mention that warranty price is a small fraction of device cost
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#005DE8]">•</span>
                Share success stories of customers who benefited from warranty
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
